import { useForm } from '@tanstack/react-form';
import { Clock, Stethoscope } from 'lucide-react';
import { z } from 'zod';

import { type GetAllReservations } from '@virtual-care/client';
import { formatReservationDateTime } from '@virtual-care/isomorphic';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@virtual-care/shadcn';
import { FormMessage, errorToFormIssue, useAction } from '@virtual-care/ui';

import { AvailableDoctorSelect } from '../../components/available-doctor-select.tsx';
import { PatientInfo } from './patient-info.tsx';

type ReservationRecord = GetAllReservations['records'][number];

interface AssignDoctorDialogProps {
  selectedReservation: ReservationRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const schema = z.object({
  doctorId: z.string().min(1, 'Please select a doctor'),
});

export function AssignDoctorDialog({
  selectedReservation,
  open,
  onOpenChange,
  onSuccess,
}: AssignDoctorDialogProps) {
  const assignDoctorMutation = useAction(
    'PATCH /reservations/{reservationId}/assign-doctor',
    {
      invalidate: ['GET /reservations'],
      onSuccess: () => {
        onOpenChange(false);
        onSuccess?.();
      },
      onError: (error) => {
        console.error('Failed to assign doctor:', error);
      },
    },
  );

  const form = useForm({
    defaultValues: {
      doctorId: '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async (props) => {
        try {
          await assignDoctorMutation.mutateAsync({
            reservationId: selectedReservation.id,
            doctorId: props.value.doctorId,
          });
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) form.reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign Doctor</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <PatientInfo patient={selectedReservation.user} />

          {/* Reservation Information */}
          <div className="bg-muted/50 space-y-3 rounded-lg p-4">
            <h3 className="flex items-center gap-2 font-medium">
              <Stethoscope className="h-4 w-4" />
              Case Information
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Speciality:</span>
              <span className="text-sm">
                {selectedReservation.speciality.name}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="text-muted-foreground h-4 w-4" />
              <span className="text-sm font-medium">
                Patient's Selected Time:
              </span>
              <span className="font-mono text-sm">
                {formatReservationDateTime(selectedReservation)}
              </span>
            </div>
          </div>

          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <form.Field
              name="doctorId"
              children={(field) => (
                <>
                  <AvailableDoctorSelect
                    field={field}
                    reservationId={selectedReservation.id}
                    label="Available Doctors"
                  />
                  <p className="text-muted-foreground text-xs">
                    Note: This will assign the doctor to the patient's selected
                    time.
                  </p>
                </>
              )}
            />

            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={!canSubmit || isSubmitting}>
                    {isSubmitting ? 'Assigning...' : 'Assign Doctor'}
                  </Button>
                </DialogFooter>
              )}
            />

            <FormMessage errors={form.state.errors} />
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
